import {axiosClient} from "@/main";
import {User} from "@/services/user";
import handleResponse from "@/services/handleResponse";
import moment from "moment";

export type Audience = "internal" | "external"
export type CountLearningHours = true | false

export type Category = { category: string, subcategory?: string };

export const LanguageLabels = {
  "sq-al": "Albanian",
  "ar-ae": "Arabic",
  "be-by": "Belarusian",
  "bn-bd": "Bengali",
  "bg-bg": "Bulgarian",
  "zh-cn": "Chinese (simplified)",
  "cmn-cn": "Chinese (Mandarin)",
  "wuu-cn": "Chinese (Wu)",
  "hr-xk": "Croatian",
  "cs-cz": "Czech",
  "da-dk": "Danish",
  "nl-nl": "Dutch",
  "en-en": "English",
  "en-ca": "English (Canada)",
  "fil-ph": "Filipino",
  "fi-fi": "Finnish",
  "fr-fr": "French",
  "fr-ca": "French (Canada)",
  "de-de": "German",
  "el-gr": "Greek",
  "hi-in": "Hindi",
  "hu-hu": "Hungarian",
  "it-it": "Italian",
  "id-id": "Indonesian",
  "ja-jp": "Japanese",
  "ko-kr": "Korean",
  "ms-bn": "Malay",
  "no-no": "Norwegian",
  "pl-pl": "Polish",
  "pt-pt": "Portuguese",
  "pt-br": "Portuguese (Brazil)",
  "ro-ro": "Romanian",
  "ru-ru": "Russian",
  "sr-rs": "Serbian",
  "sk-sk": "Slovakian",
  "sl-sl": "Slovenian",
  "es-es": "Spanish",
  "es-ar": "Spanish (Argentinia)",
  "es-co": "Spanish (Colombia)",
  "es-mx": "Spanish (Mexico)",
  "sv-se": "Swedish",
  "ta-lk": "Tamil",
  "tr-tr": "Turkish",
  "th-th": "Thai",
  "uk-ua": "Ukrainian",
  "vi-vn": "Vietnamese"
} as const

export type Language = keyof typeof LanguageLabels

export interface ApprovalStatus {
  status: Status
}

export interface Podcast {
  _key: string,
  owner: User,
  lastUpdatedAt: number,
  lastEpisodePublishedAt: string,
  createdAt: number,
  isStarred: boolean,
  isPinned: boolean,
  countLearningHours: CountLearningHours,
  audience: Audience,
  contributors: User[],
  episodeContributors: User[],
  episodeGuests: String[]
  languages: Array<Language>,
  approvalStatus?: ApprovalStatus,
  writable?: boolean,
  data: {
    imageUrl: string,
    published: boolean,
    title: string,
    description: string,
    categories: Array<Category>,
    licenseUrls: string[]
  }
}

export interface Media {
  length: number,
  mime: string,
  url: string
}

export interface Chapter {
  id: number,
  time_base: string,
  start: number,
  start_time: string,
  end: string,
  end_time: string,
  tags: {
    title: string
  }
}

export interface Episode {
  _key: string,
  owner: User,
  lastUpdatedAt: number,
  lastEpisodePublishedAt: string,
  createdAt: number,
  uploadItem?: { status: string }
  contributors: User[],
  progress: number,
  data: {
    guests: string[],
    media: {
      [key in string]: Media
    },
    language: Language,
    duration: number,
    imageUrl?: string,
    published: boolean,
    publishedAt: string,
    title: string,
    description: string
    chapters?: Chapter[],
    licenseUrls: string[],
    transcriptUrls: string[],
    seasonNumber?: number,
    episodeNumber?: number
  }
}

export const getEpisodeStatus = (episode: Episode) =>
  !episode.data.published
    ? "draft"
    : moment(episode.data.publishedAt).isBefore(moment())
    ? "published"
    : "setForPublishing";

export const getEpisodeTitleWithPrefix = (episode: Episode) =>
  (episode.data.seasonNumber != null ? `S${episode.data.seasonNumber}` : "") +
  (episode.data.episodeNumber != null ? `E${episode.data.episodeNumber}` : "") +
  ((episode.data.seasonNumber != null || episode.data.episodeNumber != null) ? ": " : "") +
    episode.data.title


export interface CategoryData {
  category: string,
  subcategories: Array<string>
}


export const getPodcasts = async (mode: "all" | "mine" | "published" | "approvals" = "all"): Promise<Array<Podcast>> => handleResponse(
  axiosClient.get(`/backend/podcasts/${mode}`)
)

export const getPodcast = async (id: string): Promise<Podcast> => handleResponse(
  axiosClient.get(`/backend/podcasts/${id}/details`)
)

export const getPodcastEpisodes = async (id: string, mode: "all" | "published" = "all"): Promise<Episode[]> => handleResponse(
  axiosClient.get(`/backend/podcasts/${id}/episodes/${mode}`)
)

export const deletePodcast = async (id: string): Promise<void> => handleResponse(
  axiosClient.delete(`/backend/podcasts/${id}`)
)

export const getPodcastEpisode = async (podcastId: string, episodeId: string): Promise<Episode> => handleResponse(
  axiosClient.get(`/backend/podcasts/${podcastId}/episodes/${episodeId}/details`)
)

export const deletePodcastEpisode = async (podcastId: string, episodeId: string): Promise<void> => handleResponse(
  axiosClient.delete(`/backend/podcasts/${podcastId}/episodes/${episodeId}`)
)

export const toggleStarPodcast = async (podcastId: string) => handleResponse(
  axiosClient.post(`/backend/podcasts/${podcastId}/toggleStar`)
)

// Sets the pin state explicitly and returns the new state. The endpoints are idempotent, so a
// retry or double submit cannot flip the state back.
export const setPinPodcast = async (podcastId: string, isPinned: boolean): Promise<boolean> => {
  const podcast = await handleResponse(
    axiosClient.post(`/backend/podcasts/${podcastId}/${isPinned ? 'pin' : 'unpin'}`)
  )
  return podcast?.isPinned === true
}

export const postListeningProgress = async (episodeId: string, progress: number) => handleResponse(
  axiosClient.post(`/backend/podcasts/episodes/${episodeId}/progress`, { progress: progress})
)

export const getCategories = async (): Promise<Array<CategoryData>> => handleResponse(
  axiosClient.get(`/backend/podcasts/categories`)
)

export interface NewPodcastDto {
  title: string,
  description: string,
  imageUrl: string,
  categories: Array<Category>,
  campaigns: Array<Category>,
  contributors: Array<string>,
  audience?: Audience,
  countLearningHours: CountLearningHours,
  licenseUrls: Array<string>
}

export interface EditPodcastDto extends NewPodcastDto {
}

export const createPodcast = async (podcast: NewPodcastDto): Promise<Podcast> => handleResponse(
  axiosClient.post(`/backend/podcasts/new`, {podcast})
)

export const editPodcast = async (podcastId: string, podcast: EditPodcastDto): Promise<Podcast> => handleResponse(
  axiosClient.put(`/backend/podcasts/${podcastId}/details`, {podcast})
)

export const sharePodcast = async (podcastId: string, recipients: string[], message?: string) => handleResponse(
  axiosClient.post(`backend/podcasts/${podcastId}/share`, {
    recipients,
    message
  })
)

export const shareEpisode = async (podcastId: string, episodeId: string, recipients: string[], message?: string) => handleResponse(
  axiosClient.post(`backend/podcasts/${podcastId}/episodes/${episodeId}/share`, {
    recipients,
    message
  })
)

export const StatusKeys = ["draft", "pending", "published", "rejected"] as const;
export type Status = typeof StatusKeys[number]

export const changePodcastStatus = async (podcastId: string, newStatus: Status, message?: string) => handleResponse(
  axiosClient.put(`backend/podcasts/${podcastId}/approval`, {
    approval: {
      status: newStatus,
      message
    }
  })
)

export const getPendingPodcastsCount = async (): Promise<number> => handleResponse(
  axiosClient.get(`/backend/user/approvals/pending`)
)

export interface NewEpisodeDto {
  title: string,
  description: string,
  imageUrl?: string,
  language: Language,
  contributors: Array<string>,
  guests: Array<string>,
  published: boolean,
  publishedAt?: string,
  licenseUrls: Array<string>,
  transcriptUrls: Array<string>,
  seasonNumber?: number,
  episodeNumber?: number
}

export const createEpisode = async (podcastId: string, episode: NewEpisodeDto): Promise<Episode> => handleResponse(
  axiosClient.post(
    `/backend/podcasts/${podcastId}/episodes`,
    {episode}
  )
)

export const editEpisode = async (podcastId: string, episodeId: string, episode: NewEpisodeDto): Promise<Episode> => handleResponse(
  axiosClient.put(
    `/backend/podcasts/${podcastId}/episodes`,
    {episode, episodeId}
  )
)

export const setEpisodeMedia = async (podcastId: string, episodeId: string, soundFile: File, onProgress?: (e: { loaded: number, total: number }) => void): Promise<void> => {
  const formData = new FormData();
  formData.append("file", soundFile);
  return handleResponse(
    axiosClient.post(
      `/backend/podcasts/${podcastId}/episodes/${episodeId}/medium`,
      formData,
      {
        onUploadProgress: onProgress,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
  )
}


